import i18n, { getLocale } from "src/i18n/i18n";
import { logError } from "src/utils/logger";

export const intlPolyfill = intlPolyfillInit();

export default async function updateLanguage(locale: string) {
    await i18n.changeLanguage(locale, () => loadLocaleData(locale));
}

export async function loadLocaleData(updatedLocale?: string) {
    if (!global.Intl) {
        await intlPolyfill.loadIntl();
    }

    const locale = updatedLocale ?? getLocale();

    // Check to see if Intl polyfill is loaded due to older Safari version (< 10)
    // If it is, load the locale data for the polyfill
    if (intlPolyfill.isIntlLoaded()) {
        await intlPolyfill.loadLocaleData(locale);
    }
}

/**
 * dynamically loads locale data for date-fns
 * NOTE: date-fns requires that locale's country code be ALL UPPERCASE and locale "en" doesn't exist
 * @param locale
 */
export async function loadDateFnsLocale(locale: string): Promise<void> {
    let loadedImport;

    try {
        switch (locale) {
            case "fr-CA":
                loadedImport = await import(
                    /* webpackMode: "lazy" */ /* webpackChunkName: "date-fns-esm-locale-fr-CA" */ `date-fns/esm/locale/fr-CA`
                );
                break;
            case "fr":
                loadedImport = await import(
                    /* webpackMode: "lazy" */ /* webpackChunkName: "date-fns-esm-locale-fr" */ `date-fns/esm/locale/fr`
                );
                break;
            case "en-CA":
                loadedImport = await import(
                    /* webpackMode: "lazy" */ /* webpackChunkName: "date-fns-esm-locale-en-CA" */ `date-fns/esm/locale/en-CA`
                );
                break;
            default:
                loadedImport = await import(
                    /* webpackMode: "lazy" */ /*  webpackChunkName: "date-fns-esm-locale-en-US" */ `date-fns/esm/locale/en-US`
                );
        }

        const { registerLocale, setDefaultLocale } = await import(
            /* webpackMode: "lazy" */ /* webpackChunkName: "react-datepicker" */ "react-datepicker"
        );

        registerLocale(locale, loadedImport.default);
        setDefaultLocale(locale);
    } catch (err) {
        logError(err);
    }
}

/**
 * Intl Polyfill for Safari version < 10 (refer to https://caniuse.com/#search=Intl)
 * Intl should only be loaded once if it doesn't exist in global
 * Intl locale data should be loaded dynamically per the current locale
 */
function intlPolyfillInit() {
    let isIntlLoaded: boolean = false;

    return {
        isIntlLoaded: () => {
            return isIntlLoaded;
        },
        loadIntl: async () => {
            try {
                await import(/* webpackMode: "lazy" */ /* webpackChunkName: "intl"*/ "intl");
                isIntlLoaded = true;
            } catch (err) {
                logError(err);
            }
        },
        loadLocaleData: async (locale: string) => {
            await loadIntlLocaleData(locale);
        },
    };
}

/**
 * dynamically loads locale data for Intl
 * NOTE: intl/locale-data/jsonp requires that locale's country code be ALL UPPERCASE in MOST cases. Some of them have MIXED CASING
 * @param locale
 */
async function loadIntlLocaleData(locale: string): Promise<void> {
    try {
        switch (locale) {
            case "fr-CA":
                await import(
                    /* webpackMode: "lazy" */ /*  webpackChunkName: "intl-locale-data-jsonp-fr-CA" */ `intl/locale-data/jsonp/fr-CA`
                );
                break;
            case "fr":
                await import(
                    /* webpackMode: "lazy" */ /*  webpackChunkName: "intl-locale-data-jsonp-fr" */ `intl/locale-data/jsonp/fr`
                );
                break;
            case "en-CA":
                await import(
                    /* webpackMode: "lazy" */ /*  webpackChunkName: "intl-locale-data-jsonp-en-CA" */ `intl/locale-data/jsonp/en-CA`
                );
                break;
            case "en-US":
                await import(
                    /* webpackMode: "lazy" */ /*  webpackChunkName: "intl-locale-data-jsonp-en-US" */ `intl/locale-data/jsonp/en-US`
                );
                break;
            default:
                await import(
                    /* webpackMode: "lazy" */ /*  webpackChunkName: "intl-locale-data-jsonp-en" */ `intl/locale-data/jsonp/en-US`
                );
        }
    } catch (err) {
        logError(err);
    }
}
